// =============================================================================
// TabBar — v2.0 | SQ-Chrome S1
// Two looks, one component: the original soft underline row, and a FluxEngine
// "board" row of solid/dashed pills under a perforated rule.
// =============================================================================
//
// 🔴 WHY A VARIANT AND NOT A RESTYLE. THIS COMPONENT CROSSES THE SEAM.
// Three consumers: CampaignDetail and CampaignContacts (FluxEngine — the hard
// board) and RecruitEdit (Front Office — the soft dashboard). Jake ruled at
// SQ-Chrome S1 that FluxEngine reads as a product WITHIN the system and the
// contrast between the two is the seam that tells an operator which product
// they are in. Restyling this component globally would push board chrome onto
// a Front Office page and erase that seam.
// 🔴 `variant` DEFAULTS TO 'soft', AND THE SOFT BRANCH BELOW IS THE ORIGINAL
// MARKUP UNCHANGED — RecruitEdit passes nothing and renders exactly as before.
// Only the two FluxEngine pages opt in.
import { pill as boardPill } from '../lib/flux/board.js';


// S90 B4: `overflow-y-hidden` is NOT redundant beside `overflow-x-auto`.
// Per CSS, when one axis is `auto` and the other is `visible`, the browser
// computes the visible one to `auto` too — so authoring only overflow-x got a
// vertical scrollbar for free.
//
// It showed up because the active tab carries `border-b-2 -mb-px`: a 40 px
// button plus a 2 px border inside a 41 px box. Measured on the campaign detail
// strip — clientHeight 41, scrollHeight 42. ONE pixel of vertical overflow, and
// one pixel is enough to paint a scrollbar inside the tab bar.
//
// Hiding the y axis rather than reconciling the -1px is deliberate: the `-mb-px`
// is load-bearing for the active-tab underline sitting on the container border.
// ⚠ THE S90 B4 NOTE ABOVE DESCRIBES THE SOFT BRANCH ONLY. Its whole mechanism
// is the 1px overlap of `border-b-2 -mb-px` against the container's 1px border,
// which is what produced the 41-vs-42 overflow. The BOARD branch has no such
// overlap — its pills carry no negative margin and do not sit on the container
// rule — so that specific 1px cannot recur there. `overflow-y-hidden` is kept
// on both anyway: it costs nothing and the reasoning above is the record of why
// authoring only `overflow-x-auto` is a trap.
export default function TabBar({ tabs, activeTab, onSelect, variant = 'soft' }) {
  if (variant === 'board') {
    return (
      <div className="flex gap-2 flex-wrap overflow-x-auto overflow-y-hidden pb-3 border-b-2 border-dashed border-[#0E2A20]">
        {tabs.map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => onSelect(tab.id)}
            className={`${boardPill(activeTab === tab.id)} whitespace-nowrap`}
          >
            {tab.label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="flex gap-1 border-b border-gray-200 overflow-x-auto overflow-y-hidden">
      {tabs.map(tab => (
        <button
          key={tab.id}
          type="button"
          onClick={() => onSelect(tab.id)}
          className={`px-4 py-2.5 text-sm font-bold font-display whitespace-nowrap transition-colors border-b-2 -mb-px ${
            activeTab === tab.id
              ? 'text-mid-green border-mid-green'
              : 'text-silver-mute border-transparent hover:text-silver-text'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}
